import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { login } from '../api/auth';
import { SESSION_REPLACED_FLAG } from '../api/client';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import { formatPhoneInput } from '../utils/phone';
import './Pages.css';

const LoginPage: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // ISS-062 — 다른 위치 로그인으로 강제 종료된 경우 모달 표시.
  // 토큰은 이미 axios 인터셉터가 폐기한 상태. 모달은 정보 전달용 — 닫지 않아도 로그아웃 진행 완료.
  const [sessionReplacedOpen, setSessionReplacedOpen] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_REPLACED_FLAG)) {
      sessionStorage.removeItem(SESSION_REPLACED_FLAG);
      setSessionReplacedOpen(true);
      return;
    }
    const msg = sessionStorage.getItem('auth_redirect_message');
    if (msg) {
      sessionStorage.removeItem('auth_redirect_message');
      toast.warning(msg);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = await login(phone, password);
      loginWithToken(token);
      navigate(token.role === 'user' ? '/my-events' : '/');
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const msg = (err.response?.data as { error?: string } | undefined)?.error;
        // ISS-024: 비활성 계정 차단(403)은 warning 토스트로 구분 표시
        if (status === 403 && msg) {
          toast.warning(msg);
        } else {
          toast.error(msg || '전화번호 또는 비밀번호가 올바르지 않습니다');
        }
      } else {
        toast.error('전화번호 또는 비밀번호가 올바르지 않습니다');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* ISS-062 — 세션 강제 종료 안내 모달. 확인 버튼 클릭 안 해도 로그아웃은 이미 완료된 상태. */}
      <Modal
        isOpen={sessionReplacedOpen}
        onClose={() => setSessionReplacedOpen(false)}
        title="세션 종료"
        onConfirm={() => setSessionReplacedOpen(false)}
        confirmText="확인"
        cancelText="닫기"
      >
        <p style={{ margin: 0 }}>새로운 세션에서 로그인이 되었습니다.</p>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#666' }}>보안을 위해 이전 세션은 종료되었습니다.</p>
      </Modal>
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="login-logo">NUCODE</div>
        <h2 className="login-title">차량 과속 모니터링</h2>
        <div className="form-group">
          <label>전화번호</label>
          <input type="tel" value={phone} onChange={e => setPhone(formatPhoneInput(e.target.value))} placeholder="010-0000-0000" maxLength={13} required />
        </div>
        <div className="form-group">
          <label>비밀번호</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="비밀번호 입력" required />
        </div>
        <button type="submit" className="btn btn--primary login-btn" disabled={loading}>
          {loading ? '로그인 중...' : '로그인'}
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 14 }}>
          <Link to="/signup">회원가입</Link>
          <Link to="/forgot-password">비밀번호 찾기</Link>
        </div>
      </form>
    </div>
  );
};

export default LoginPage;
